import type { Permission, Role } from "@byfluent/domain";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { Spinner } from "../components/ui";
import { ApiError, api } from "./api";

export interface Me {
  id: string;
  email: string;
  name: string;
  roles: Role[];
  permissions: Permission[];
  totpConfirmed: boolean;
  totpRequired: boolean;
}

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<Me>("/v1/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function homeFor(me: Me): string {
  if (me.roles.includes("admin")) return "/admin";
  if (me.roles.includes("seller")) return "/vendas";
  if (me.roles.includes("teacher")) return "/professor";
  return "/entrar";
}

/** Guarda das áreas da equipe: exige sessão, TOTP quando obrigatório e a função da área. */
export function RequireRole({ need, children }: { need: Role; children: ReactNode }) {
  const { data: me, isLoading } = useMe();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="center">
        <Spinner />
      </div>
    );
  }
  if (!me)
    return <Navigate to={`/entrar?voltar=${encodeURIComponent(location.pathname)}`} replace />;
  if (me.totpRequired && !me.totpConfirmed) return <Navigate to="/entrar/autenticador" replace />;
  if (!me.roles.includes(need) && !me.roles.includes("admin"))
    return <Navigate to={homeFor(me)} replace />;
  return <>{children}</>;
}
